import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Anchor, Mail, Loader2, ArrowLeft, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BACKGROUND_IMAGE = 'https://images.pexels.com/photos/3808621/pexels-photo-3808621.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email, origin_url: window.location.origin });
      setSubmitted(true);
      toast.success('Check your email for reset instructions');
    } catch (error) {
      // Still show success to prevent email enumeration
      setSubmitted(true);
      toast.success('Check your email for reset instructions');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${BACKGROUND_IMAGE})` }}
      />
      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-[2px]" />
      
      {/* Content */}
      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/logo.png" alt="Seafarer Logbook" className="h-16 w-16 mx-auto mb-4" />
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Seafarer Logbook</h1>
          <p className="text-slate-300 mt-1">Your trips, earnings & tax — all in one place</p>
        </div>

        <Card className="bg-slate-800/90 border-slate-700 backdrop-blur-sm shadow-2xl">
          <CardHeader className="pb-4">
            <CardTitle className="text-white text-lg sm:text-xl">
              {submitted ? 'Check Your Email' : 'Forgot Password'}
            </CardTitle>
            <CardDescription className="text-slate-400 text-sm">
              {submitted 
                ? 'We\'ve sent password reset instructions to your email address.'
                : 'Enter your email address and we\'ll send you a link to reset your password.'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {submitted ? (
              <div className="space-y-6">
                <div className="flex justify-center">
                  <div className="p-4 bg-amber-500/20 rounded-full">
                    <CheckCircle2 className="h-12 w-12 text-amber-400" />
                  </div>
                </div>
                <div className="text-center space-y-2">
                  <p className="text-slate-300 text-sm">
                    If an account exists for <span className="font-medium text-white">{email}</span>, 
                    you will receive an email with instructions to reset your password.
                  </p>
                  <p className="text-slate-400 text-xs">
                    The link will expire in 1 hour.
                  </p>
                </div>
                <div className="space-y-3">
                  <Button 
                    variant="outline"
                    className="w-full border-slate-600 text-slate-300 hover:bg-slate-700 h-11"
                    onClick={() => {
                      setSubmitted(false);
                      setEmail('');
                    }}
                    data-testid="try-another-email-btn"
                  >
                    Try another email
                  </Button>
                  <Link to="/login" className="block">
                    <Button 
                      variant="ghost"
                      className="w-full text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 h-11"
                      data-testid="back-to-login-btn"
                    >
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Back to Sign In
                    </Button>
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300 text-sm">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 bg-slate-900/80 border-slate-600 text-white placeholder:text-slate-500 h-11"
                      required
                      data-testid="forgot-password-email-input"
                    />
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-amber-600 hover:bg-amber-700 h-11 text-base font-medium shadow-lg shadow-amber-600/20"
                  disabled={loading}
                  data-testid="forgot-password-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </Button>
                <Link to="/login" className="block">
                  <Button 
                    type="button"
                    variant="ghost"
                    className="w-full text-slate-400 hover:text-slate-300 hover:bg-slate-700/50 h-11"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to Sign In
                  </Button>
                </Link>
              </form>
            )}
          </CardContent>
        </Card>
        
        {/* Footer */}
        <p className="text-center text-slate-500 text-xs mt-6">
          Track your qualifying days for UK tax relief
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;
